import { useState, type ReactNode } from 'react';
import { T, Var, useGT } from 'gt-react';
import { CaretDownIcon } from '@phosphor-icons/react';
import type { APInvoice, PreviewedInvoiceExceptions } from '@erp/rpc';
import { usePreviewAPInvoiceExceptions } from '@/api/rpc/hooks/accounts-payable/usePreviewAPInvoiceExceptions';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ExceptionRuleSetPicker } from './ExceptionRuleSetPicker';
import {
  errorText,
  evaluatorCallState,
  firstPreviewResult,
  previewVerdict,
  unroutedRaised,
} from './testExceptionRules';

/**
 * Dry-run dialog for one invoice. The slide-in header menu opens this;
 * nothing is persisted.
 */
export function TestExceptionRulesDialog({
  invoice,
  open,
  onOpenChange,
}: {
  invoice: APInvoice;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        {open && <TestExceptionRulesBody invoice={invoice} />}
      </DialogContent>
    </Dialog>
  );
}

function TestExceptionRulesBody({ invoice }: { invoice: APInvoice }) {
  const gt = useGT();
  const preview = usePreviewAPInvoiceExceptions();
  const [ruleSetId, setRuleSetId] = useState<string | null>(null);
  const result = firstPreviewResult(preview.data);

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <T context="dialog title: dry-run exception rules against one invoice">
            Test Exception Rules
          </T>
        </DialogTitle>
        <DialogDescription>
          <T context="dialog description for the exception rules dry run">
            Runs the evaluator against invoice{' '}
            <Var>{invoice.invoiceNumber ?? invoice.id.slice(0, 8)}</Var> using
            the rule set you pick. Nothing is saved: no exceptions open, no
            tasks move, no workflows start.
          </T>
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2">
        <ExceptionRuleSetPicker
          ruleSetId={ruleSetId}
          isRunning={preview.isPending}
          onChange={setRuleSetId}
          onRun={(id) =>
            preview.mutate({
              invoiceIds: [invoice.id],
              exceptionRuleSetId: id,
            })
          }
          copy={{
            fieldLabel: (
              <T context="label for the rule set picker in the dry-run dialog">
                Rule set
              </T>
            ),
            loadingPlaceholder: gt('Loading rule sets…', {
              context: 'dry-run dialog: rule set picker loading',
            }),
            selectPlaceholder: gt('Select a rule set…', {
              context: 'dry-run dialog: rule set picker placeholder',
            }),
            searchPlaceholder: gt('Search rule sets…', {
              context: 'dry-run dialog: rule set picker search',
            }),
            emptyHint: (
              <T context="dry-run dialog: no rule sets exist yet">
                No rule sets yet — create one under Accounts Payable → Exception
                Rules.
              </T>
            ),
            runBusy: (
              <T context="dry-run dialog: run button while running">Running…</T>
            ),
            runIdle: <T context="dry-run dialog: run button">Run</T>,
          }}
        />
        {preview.isError && (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
            {errorText(preview.error)}
          </p>
        )}
        {result && <PreviewResult result={result} />}
      </div>
    </>
  );
}

function PreviewResult({ result }: { result: PreviewedInvoiceExceptions }) {
  const verdict = previewVerdict(result);
  const unrouted = unroutedRaised(result);
  return (
    <div className="space-y-3">
      <VerdictBanner result={result} verdict={verdict} />

      <section className="space-y-2">
        <h4 className="text-sm font-medium">
          <T context="dry-run dialog: heading over the exceptions that fired">
            Raised (<Var>{result.raised.length}</Var>)
          </T>
        </h4>
        {result.raised.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            <T context="dry-run dialog: nothing fired">
              None of the <Var>{result.enumName}</Var> types apply to this
              invoice.
            </T>
          </p>
        ) : (
          <ul className="space-y-2">
            {result.raised.map((exception) => (
              <li
                key={exception.exceptionTypeOptionId}
                className="rounded-md border p-2.5"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{exception.name}</span>
                  {!exception.hasRule && (
                    <Badge variant="outline" className="text-amber-600">
                      <T context="badge: raised type has no routing rule">
                        No rule
                      </T>
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {exception.reasoning}
                </p>
              </li>
            ))}
          </ul>
        )}
        {unrouted.length > 0 && (
          <p className="text-xs text-amber-600">
            <T context="dry-run dialog: some raised types have no rule">
              <Var>{unrouted.length}</Var> raised type(s) have no rule in this
              set — they would open unassigned and still block.
            </T>
          </p>
        )}
        {result.invalidSelectionCount > 0 && (
          <p className="text-xs text-muted-foreground">
            <T context="dry-run dialog: model returned ids outside the vocabulary">
              The model also returned <Var>{result.invalidSelectionCount}</Var>{' '}
              id(s) outside the vocabulary; they were dropped.
            </T>
          </p>
        )}
      </section>

      <Collapsible>
        <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <CaretDownIcon className="h-3 w-3" />
          <T context="dry-run dialog: toggle listing types that did not fire">
            Not raised (<Var>{result.notRaised.length}</Var>)
          </T>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <p className="mt-1 text-xs text-muted-foreground">
            {result.notRaised.map((type) => type.name).join(', ') || '—'}
          </p>
        </CollapsibleContent>
      </Collapsible>

      <EvaluatorInput result={result} />
    </div>
  );
}

function EvaluatorInput({ result }: { result: PreviewedInvoiceExceptions }) {
  const callState = evaluatorCallState(result);
  return (
    <section className="space-y-2">
      <h4 className="text-sm font-medium">
        <T context="dry-run dialog: heading over the exact LLM payload">
          What the model received
        </T>
      </h4>
      {callState === 'skipped-empty-vocabulary' ? (
        <p className="text-xs text-amber-600">
          <T context="dry-run dialog: LLM skipped because the enum has no options">
            The model was not called — this enum has no options.
          </T>
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          <T context="dry-run dialog: confirm the evaluator LLM ran">
            The evaluator LLM was called with the system instructions and prompt
            below.
          </T>
        </p>
      )}
      <PromptCollapsible
        title={
          <T context="dry-run dialog: toggle showing the evaluator system prompt">
            System instructions
          </T>
        }
        text={result.systemPrompt}
      />
      <PromptCollapsible
        title={
          <T context="dry-run dialog: toggle showing the evaluator user prompt">
            Prompt (vocabulary + invoice facts)
          </T>
        }
        text={result.userPrompt}
      />
    </section>
  );
}

function PromptCollapsible({
  title,
  text,
}: {
  title: ReactNode;
  text: string;
}) {
  return (
    <Collapsible>
      <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <CaretDownIcon className="h-3 w-3" />
        {title}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <pre className="mt-1 max-h-80 overflow-auto rounded-md bg-muted p-3 text-[11px] leading-snug whitespace-pre-wrap">
          {text}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
}

function VerdictBanner({
  result,
  verdict,
}: {
  result: PreviewedInvoiceExceptions;
  verdict: ReturnType<typeof previewVerdict>;
}) {
  if (verdict === 'clear') {
    return (
      <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm dark:border-green-900 dark:bg-green-950/30">
        <T context="dry-run dialog: verdict when no exceptions fire">
          No exceptions. A live pass would let this invoice proceed to Purchase
          Match Ready.
        </T>
      </div>
    );
  }
  const owner = result.owner;
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
      {owner ? (
        <T context="dry-run dialog: verdict when exceptions fire and a rule owns them">
          Blocked. The <Var>{owner.exceptionTypeName}</Var> rule would hand this
          invoice to <Var>{owner.type}</Var> <Var>{owner.name ?? owner.id}</Var>
          .
        </T>
      ) : (
        <T context="dry-run dialog: verdict when exceptions fire but no rule routes them">
          Blocked, but no rule in <Var>{result.ruleSetName}</Var> covers any
          raised type — the invoice would sit unassigned.
        </T>
      )}
    </div>
  );
}
